import { useGetEventFeatureFlagsQuery } from '@graphql/generated';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useCallback } from 'react';

/**
 * @deprecated Use `useIsFeatureEnabled({ eventFeatureFlagKey: 'flag' })` instead.
 * This hook will be removed in a future version.
 *
 * The new unified API provides:
 * - Type-safe event feature flag keys
 * - Consistent return signature with other feature flag types
 * - Automatic eventHandle from context (no need to pass it)
 * - Query param override support built-in
 *
 * @example
 * // Old approach:
 * const { isEventFeatureFlagEnabled, loadingEventFeatureFlags } = useEventFeatureFlags({ eventHandle, skip });
 * const isPaid = isEventFeatureFlagEnabled('manuallyPaidEvent');
 *
 * // New approach:
 * const [isPaid, isLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'manuallyPaidEvent', skip });
 */
export const useEventFeatureFlags = ({ eventHandle, skip }: { eventHandle: string; skip?: boolean }) => {
  const { data, loading } = useGetEventFeatureFlagsQuery({
    variables: { eventHandle },
    batchMode: 'fast',
    skip,
  });

  const { getValueString } = useQueryParamHelper();
  const isEventFeatureFlagEnabledViaQueryParam = useCallback(
    (flag: string) => {
      return getValueString(`feature.${flag}`) === 'true';
    },
    [getValueString],
  );

  const isEventFeatureFlagEnabled = useCallback(
    (flag: string) => {
      return data?.eventByName?.featureFlags?.includes(flag) || isEventFeatureFlagEnabledViaQueryParam(flag);
    },
    [data, isEventFeatureFlagEnabledViaQueryParam],
  );

  return {
    loadingEventFeatureFlags: loading,
    hasEventFeatureFlagData: !!data?.eventByName,
    isEventFeatureFlagEnabled,
  };
};
